/**
 * RosterList — vertical list of player slots.
 *
 * Each slot is a chalk-ruled line; filled slots show "1. NAME" in their
 * pigment colour. Empty slots render as faint chalk dashes so the host
 * can see how many seats remain.
 */

import { surfaces, fontStacks, playerColors } from '@qd/design-tokens';
import type { LobbyPlayer, PlayerColorKey } from '@qd/protocol';
import { useIsMobile } from '../../hooks/useIsMobile';

interface RosterListProps {
  players: readonly LobbyPlayer[];
  maxPlayers: number;
}

export function RosterList({ players, maxPlayers }: RosterListProps) {
  const isMobile = useIsMobile();
  const slots: Array<LobbyPlayer | null> = [];
  for (let i = 0; i < maxPlayers; i += 1) {
    slots.push(players[i] ?? null);
  }
  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 10 : 14,
        width: '100%',
        maxWidth: 380,
      }}
    >
      {slots.map((slot, index) => (
        <RosterSlot key={index} index={index} player={slot} isMobile={isMobile} />
      ))}
    </ul>
  );
}

interface RosterSlotProps {
  index: number;
  player: LobbyPlayer | null;
  isMobile: boolean;
}

function RosterSlot({ index, player, isMobile }: RosterSlotProps) {
  const ordinal = `${index + 1}`;
  if (!player) {
    return (
      <li
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 8 : 12,
          padding: isMobile ? '10px 12px' : '12px 16px',
          background: 'rgba(0,0,0,0.1)',
          border: `2px dashed rgba(255,255,255,0.2)`,
          borderRadius: 16,
          fontFamily: fontStacks.display,
          color: surfaces.paper,
          opacity: 0.5,
          fontSize: isMobile ? 16 : 18,
          boxShadow: 'inset 0 4px 6px rgba(0,0,0,0.1)',
        }}
      >
        <span style={{ width: isMobile ? 22 : 28, opacity: 0.5 }}>{ordinal}.</span>
        <span>Waiting for player...</span>
      </li>
    );
  }

  const tone =
    player.color != null
      ? playerColors[player.color as PlayerColorKey].hex
      : surfaces.chalk;

  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 8 : 12,
        padding: isMobile ? '10px 12px' : '12px 16px',
        background: tone,
        borderRadius: 16,
        fontFamily: fontStacks.display,
        fontSize: isMobile ? 17 : 20,
        fontWeight: 800,
        color: '#ffffff',
        boxShadow: `0 6px 0 rgba(0,0,0,0.2), inset 0 2px 4px rgba(255,255,255,0.3)`,
        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
      }}
    >
      <span style={{ opacity: 0.8 }}>{ordinal}.</span>
      <span
        style={{
          flex: 1,
          textTransform: 'uppercase',
          // Long names + LEAD/READY badges can blow the slot width on
          // narrow viewports — guarantee the badges stay visible by
          // truncating the name with an ellipsis.
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {player.name}
      </span>
      {player.isAdmin && <Badge label="LEAD" />}
      {player.isReady ? <Badge label="READY" tint="#fff" /> : <Badge label="WAITING..." muted />}
    </li>
  );
}

interface BadgeProps {
  label: string;
  tint?: string;
  muted?: boolean;
}

function Badge({ label, muted }: BadgeProps) {
  return (
    <span
      style={{
        fontFamily: fontStacks.display,
        fontSize: 12,
        fontWeight: 900,
        padding: '4px 10px',
        background: muted ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
        borderRadius: 8,
        color: '#ffffff',
        opacity: muted ? 0.7 : 1,
        textShadow: 'none',
      }}
    >
      {label}
    </span>
  );
}
